import consola from "consola";
import axios from "axios";
import * as authActions from "../store/actions/authActions";
import * as accountActions from "../store/actions/accountActions";
import { store } from "../store/configureStore";
import i18next from "i18next";

export const BASE_URI = process.env.REACT_APP_BACKEND;

const CLIENT_ID = process.env.REACT_APP_OAUTH_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_OAUTH_CLIENT_SECRET;

const insAxios = axios.create();
export const expireInDay = 30;

/**
 * Requête API prêt à utiliser
 * @param {String} endPoint Fin d'url
 * @param {Object} payload Paramètres à passer
 * @param {String} method get / post / put / ...
 * @param {Object} headers Header supplémentaire à passer
 */
export function axiosApi(
  endPoint,
  payload = {},
  method = "get",
  headers = {},
  options = {}
) {
  const language = i18next.language ? i18next.language : process.env.REACT_APP_DEFAULT_LOCALE;
  const locale = language.replace(/-/g, "_");
  payload._locale = locale;

  let config = {
    method: method,
    url: `${BASE_URI}api/${endPoint}`,
    headers: headers,
    ...options,
  };
  if (method === "get") {
    config.params = payload;
  } else {
    config.data = payload;
  }

  return insAxios(config);
}

const simplifyResponse = (response) => {
  if (response.data.data) return response.data.data;
  else return response.data;
};

const simplifyError = (error) => {
  consola.error("Got an error from API: ", error.response.config.url);
  if (error.response.data.flow && error.response.data.flow === "external") {
    return error.response.data;
  }
  if (undefined !== error.response.data.detail) return error.response.data.detail;
  if (error.response.status === 500) return "errors.error_500";
  if (error.response.data.data && error.response.data.data.user_error)
    return error.response.data.data.user_error;
  if (error.response.status === 409)
    return { ...error.response.data, errors_status: 409 };
  else return error.response.data;
};

/**
 * On intercepte la requête ici
 */
insAxios.interceptors.request.use(
  (config) => {
    // Récupère l'AccessToken
    const access_token = store.getState().auth.access_token;

    // Avant requête API on vérifie l'expiration de nos tokens
    const expire_date = store.getState().auth.expire_date;
    if (expire_date && expire_date < Date.now()) {
      // On retourne une promesse qui ne sera jamais résolu
      return new Promise((resolve) => {
        // Suppression REMEMBER ME email et password + deconnexion
        store.dispatch(authActions.delRememberMe());
        logout();
      });
    }

    // Headers par default
    const defaultHeaders = {
      Accept: "application/json",
      "Content-Type": "application/json;charset=utf-8",
    };

    if (!config.ispublic) {
      defaultHeaders["Authorization"] = `Bearer ${access_token}`;
    }

    return {
      ...config,
      headers: Object.assign(defaultHeaders, config.headers),
    };
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

insAxios.interceptors.response.use(
  (response) => {
    return simplifyResponse(response);
  },
  (err) => {
    const originalRequest = err.config;
    const refreshToken = store.getState().auth.refresh_token;

    if (err.response.status === 401 && !originalRequest._retry && null !== refreshToken) {
      const currentBearer = "Bearer " + store.getState().auth.access_token;
      const originalBearer = originalRequest.headers["Authorization"];

      if (isRefreshing || originalBearer !== currentBearer) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return insAxios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        refreshAccessToken(refreshToken)
          .then(({ data }) => {
            const { access_token, refresh_token } = data;
            const expire_date = Date.now() + expireInDay * 1000 * 60 * 60 * 24;

            // Mettre à jour les tokens dans redux
            store.dispatch(
              authActions.setTokens({
                access_token: access_token,
                refresh_token: refresh_token,
                expire_date,
              })
            );

            originalRequest.headers["Authorization"] = "Bearer " + access_token;
            processQueue(null, access_token);
            resolve(axios(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            logout();
            reject(err);
          })
          .then(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(simplifyError(err));
  }
);

const refreshAccessToken = (refreshToken) => {
  return axios({
    method: "post",
    url: `${BASE_URI}api/oauth/v2/token`,
    data: {
      grant_type: "refresh_token",
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      refresh_token: refreshToken,
    },
  });
};

/**
 * Connexion
 */
export function login(username, password, rememberMe = false) {
  return new Promise((resolve, reject) => {
    let obj = {
      grant_type: "password",
      client_id: CLIENT_ID,
      client_secret: CLIENT_SECRET,
      username: username,
      password: password,
    };
    axiosApi("oauth/v2/token", obj, "post", {}, { ispublic: true }).then(
      (data) => {
        axiosApi("account", {}, "get", {
          Authorization: `Bearer ${data.access_token}`,
        }).then(
          (dataUser) => {
            // Fonction de connexion également dans le composant SsoPage
            // Temps d'expiration
            const expire_date = Date.now() + expireInDay * 1000 * 60 * 60 * 24;
            const expires_in = Date.now() + data.expires_in;
            // const expire_date = Date.now() + 10000

            // stockage tokens dans redux
            store.dispatch(
              authActions.setTokens({
                ...data,
                expire_date: expire_date,
                expires_in: expires_in,
              })
            );
            // stockage / suppression rememberMe access
            if (rememberMe) {
              store.dispatch(authActions.setRememberMe(username, password));
            } else {
              store.dispatch(authActions.delRememberMe());
            }
            // stockage user dans redux
            store.dispatch(accountActions.setAccountDatas(data));
            resolve(dataUser);
          },
          (error) => {
            reject(error);
          }
        );
      },
      (error) => {
        reject(error);
      }
    );
  });
}

/**
 * Déconnexion
 */
export function logout() {
  return new Promise((resolve) => {
    // suppr tokens dans redux
    store.dispatch(authActions.delTokens());
    store.dispatch(accountActions.delAccountDatas());

    resolve(null);
  });
}

export function fakeRequest(success = true, delay = 250) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (success) {
        resolve(true);
      } else {
        reject(false);
      }
    }, delay);
  });
}

let redirectUrl = "/";

export const redirectPath = {
  setRedirectUrl: (url) => {
    redirectUrl = !url ? redirectUrl : url;
  },
  getRedirectUrl: () => {
    return redirectUrl;
  },
};
